/**
 * Form mapping utilities for the Service Center email template form
 * (legacy: mortgageboss_web/.../template_management/service_center).
 *
 * This first pass covers the "Template Details" section only. The detail GET
 * returns the record wrapped in an array (`{ emailManagement: [record] }`);
 * `mapToInitialData` unwraps it. Fields not shown in Template Details are
 * carried through `TemplateInitialData` so they survive a save (the legacy
 * edit payload merges the existing record back in).
 */
import {
    DEFAULT_TEMPLATE_OBJECT_TYPE,
    REMINDER_RECURRENCE_DAILY,
    TEMPLATE_SAVE_TEMPLATE_TYPE,
} from '@/constants/templateOptions'
import type {
    ReminderResponse,
    SaveEmailStatusPreferencePayload,
    SaveReminderPayload,
    SaveTemplateRequest,
    TemplateDetail,
    TemplateDetailResponse,
} from '@/types/template.types'

/** Form values used by react-hook-form / the Zod resolver. */
export interface TemplateFormData {
    objectTypeID: number
    emailTypeID: number
    templateName: string
    /** 'private' | 'public' — modeled as a string for radio compatibility */
    templateType: 'private' | 'public'
    /** true = inactive (status checkbox checked) */
    inactive: boolean
    templateDesc: string
    templateSubject: string
    // -- Communication & Recipients --
    brokerChecked: boolean
    laAgentChecked: boolean
    forDRCAgent: boolean
    isCustomizeComm: boolean
    // -- Email body (per recipient, inlined HTML from the GrapesJS editor) --
    templateBody: string
    forBrokerTemplate: string
    forLATemplate: string
    forDRCTemplate: string
    // -- Task Creation --
    taskCreation: boolean
    // -- Reminder --
    isSendingReminder: boolean
    reminderRecurrenceId: number
    reminderRecurForDays: number
    reminderInstances: number
    // -- NLD/DRC statuses (persisted separately via savePreference, never sent
    //    to /api/EmailManagement). --
    statusIds: number[]
    // -- Attachments (newly-staged files; uploaded after save, never sent to
    //    /api/EmailManagement). Existing attachments load separately via
    //    useEmailTemplateAttachments and render as `existingAttachments`. --
    attachments: File[]
}

/** Extra read-only fields needed by the edit page (carried alongside form data). */
export interface TemplateInitialData extends TemplateFormData {
    /** Owning employee — the detail endpoint's ownership field (see TemplateDetail). */
    employeeId?: number
    /** Existing reminder id (0 when none yet), preserved across save. */
    reminderId?: number
}

/** Default values for create mode. Mirrors the legacy `defaultTemplateDetails`. */
export const EMPTY_TEMPLATE_FORM: TemplateFormData = {
    objectTypeID: DEFAULT_TEMPLATE_OBJECT_TYPE,
    // 0 is the legacy "unselected" sentinel — validation forces a real pick.
    emailTypeID: 0,
    templateName: '',
    templateType: 'private',
    inactive: false,
    templateDesc: '',
    templateSubject: '',
    brokerChecked: false,
    laAgentChecked: false,
    forDRCAgent: false,
    isCustomizeComm: false,
    templateBody: '',
    forBrokerTemplate: '',
    forLATemplate: '',
    forDRCTemplate: '',
    taskCreation: false,
    isSendingReminder: false,
    reminderRecurrenceId: REMINDER_RECURRENCE_DAILY,
    reminderRecurForDays: 0,
    reminderInstances: 1,
    statusIds: [],
    attachments: [],
}

/**
 * Map an API detail response (and optionally the separately-loaded reminder)
 * to the form's initial data.
 *
 * The recipient checkboxes are derived from whether each per-recipient body is
 * non-empty — a deliberate improvement over the legacy form, which always
 * started them unchecked on load.
 */
export function mapToInitialData(
    response: TemplateDetailResponse,
    reminder?: ReminderResponse,
    statusIds: number[] = [],
): TemplateInitialData {
    const record: TemplateDetail | undefined = response.emailManagement?.[0]
    const r = reminder?.reminder ?? null
    return {
        objectTypeID: record?.objectTypeID ?? DEFAULT_TEMPLATE_OBJECT_TYPE,
        emailTypeID: record?.emailTypeID ?? 0,
        templateName: record?.templateName ?? '',
        templateType: record?.templateIsShared ? 'public' : 'private',
        inactive: record?.inactive ?? false,
        templateDesc: record?.templateDesc ?? '',
        templateSubject: record?.templateSubject ?? '',
        // Communication & Recipients
        brokerChecked: record?.brokerChecked ?? !!record?.forBrokerTemplate,
        laAgentChecked: record?.laAgentChecked ?? !!record?.forLATemplate,
        forDRCAgent: record?.forDRCAgent ?? !!record?.forDRCTemplate,
        isCustomizeComm: record?.isCustomizeComm ?? false,
        // Task Creation
        taskCreation: record?.taskCreation ?? false,
        // Reminder
        isSendingReminder: r?.isSendingReminder ?? false,
        reminderRecurrenceId: r?.recurrenceId ?? REMINDER_RECURRENCE_DAILY,
        reminderRecurForDays: r?.recurForDays ?? 0,
        reminderInstances: r?.instances ?? 1,
        // Email body — now editable form fields (load existing HTML, default empty).
        templateBody: record?.templateBody ?? '',
        forBrokerTemplate: record?.forBrokerTemplate ?? '',
        forLATemplate: record?.forLATemplate ?? '',
        forDRCTemplate: record?.forDRCTemplate ?? '',
        statusIds,
        // Attachments staged in the form start empty; existing files load via
        // useEmailTemplateAttachments and are shown separately.
        attachments: [],
        // Passthrough
        employeeId: record?.employeeId,
        reminderId: r?.reminderId ?? 0,
    }
}

/**
 * Whether saving is blocked for the current user. Reproduces the legacy
 * `generateSaveDisableStatus` (mortgageboss_web/.../service_center/TemplateManagement.js):
 *
 * - Create mode: never blocked.
 * - Edit mode: allowed when the user owns the template, OR has the `optionHub`
 *   grant and the email type is NLD, OR has `optionDealReview` and the email
 *   type is DRC. Otherwise blocked.
 *
 * Scope matters: legacy gates only the **Save button, the attachment
 * upload/delete, and the body editor** with this flag. The Template Details,
 * Communication & Recipients, Reminder, and Task Creation inputs are never
 * disabled — `TemplateCreation.js` carries no `disabled` prop at all. Disabling
 * those inputs was the regression in AB#1723, so do not widen this flag's use.
 *
 * Two legacy conjuncts live elsewhere:
 * - The "≥1 LA/DRC status selected" conjunct (`LA_DRC_selected`) depends on
 *   live form state, so it is `isStatusRequiredToSave` and gates only the Save
 *   button. Folding it in here would also lock the body editor and attachments
 *   while no status is ticked (AB#2517).
 * - The "≥1 recipient selected" requirement is save-time validation, not a
 *   permission gate.
 *
 * `emailType` is the display label resolved from `emailTypeID` via
 * `useEmailTypes()`. Legacy compares it strictly against
 * "National Lender Desk (NLD)" / "Deal Review Centre (DRC)"; we substring-match
 * the NLD/DRC tokens instead, because the label now comes from a different
 * source field (`EmailTemplateTypeList.nameEn`) and may differ in whitespace.
 */
export function isTemplateSaveDisabled(args: {
    mode: 'create' | 'edit'
    currentUserId?: number | string
    /** Owning employee id — `emailManagement[0].employeeId` on the detail response. */
    employeeId?: number | string
    /** Email type display string, e.g. "National Lender Desk (NLD)" / "Deal Review Centre (DRC)". */
    emailType?: string
    optionHub?: boolean
    optionDealReview?: boolean
}): boolean {
    const { mode, currentUserId, employeeId, emailType, optionHub, optionDealReview } = args
    if (mode === 'create') return false

    const isOwner =
        employeeId != null && currentUserId != null && String(employeeId) === String(currentUserId)

    const canSave = isOwner || hasNldDrcGrant({ emailType, optionHub, optionDealReview })
    return !canSave
}

/** `optionHub` on an NLD template, or `optionDealReview` on a DRC template. */
function hasNldDrcGrant(args: {
    emailType?: string
    optionHub?: boolean
    optionDealReview?: boolean
}): boolean {
    const { emailType, optionHub, optionDealReview } = args
    const isNLD = !!emailType?.includes('NLD')
    const isDRC = !!emailType?.includes('DRC')
    return (!!optionHub && isNLD) || (!!optionDealReview && isDRC)
}

/**
 * Whether Save needs at least one NLD/DRC status ticked. The `LA_DRC_selected`
 * conjunct of legacy `generateSaveDisableStatus` (TemplateManagement.js:679-688):
 *
 * - Create mode: always required.
 * - Edit mode: required unless the NLD/DRC grant lets the user save, so the
 *   owner needs a status but a Hub user on an NLD template (or a Deal Review
 *   user on a DRC template) does not.
 *
 * The picker in Send Communication joins on the status rows, so a template
 * saved with none is never offered.
 */
export function isStatusRequiredToSave(args: {
    mode: 'create' | 'edit'
    /** Email type display string, as for `isTemplateSaveDisabled`. */
    emailType?: string
    optionHub?: boolean
    optionDealReview?: boolean
}): boolean {
    const { mode, ...grant } = args
    if (mode === 'create') return true
    return !hasNldDrcGrant(grant)
}

/**
 * Whether the user may access the LA/DRC (Letter of Approval / Deal Review Centre)
 * template surface. Mirrors legacy `Settings.js` (`optionHub || optionDealReview`):
 * either the Hub grant or the Deal-Review grant is sufficient. Task Templates are
 * always accessible and are intentionally not gated by this rule.
 */
export function canAccessLaDrcTemplates(
    employee: { optionHub?: boolean | null; optionDealReview?: boolean | null } | undefined,
): boolean {
    return Boolean(employee?.optionHub || employee?.optionDealReview)
}

/** Map form data back to the API payload shape (wrapped under `emailManagement`). */
export function mapFormToPayload(
    data: TemplateFormData,
    extra: { id?: number } = {},
): SaveTemplateRequest {
    return {
        emailManagement: {
            ...(extra.id ? { id: extra.id } : {}),
            templateName: data.templateName.trim(),
            templateSubject: data.templateSubject.trim(),
            templateDesc: data.templateDesc.trim(),
            objectTypeID: data.objectTypeID,
            emailTypeID: data.emailTypeID,
            templateIsShared: data.templateType === 'public',
            inactive: data.inactive,
            templateType: TEMPLATE_SAVE_TEMPLATE_TYPE,
            // Communication & Recipients + Task Creation flags.
            brokerChecked: data.brokerChecked,
            laAgentChecked: data.laAgentChecked,
            forDRCAgent: data.forDRCAgent,
            isCustomizeComm: data.isCustomizeComm,
            taskCreation: data.taskCreation,
            // Per-recipient email bodies, edited via the GrapesJS editor and
            // stored as inlined HTML (legacy contract).
            templateBody: data.templateBody,
            forBrokerTemplate: data.forBrokerTemplate,
            forLATemplate: data.forLATemplate,
            forDRCTemplate: data.forDRCTemplate,
        },
    }
}

/**
 * Compute the reminder end date (Unix seconds) from recur/instances, porting
 * the legacy `generateEndDate` formula (Reminder.js). Not user-entered; sent
 * with the reminder payload to mirror legacy behavior.
 */
export function computeReminderEndDate(recurForDays: number, instances: number): number {
    const inst = instances || 1
    let addDays = 0
    if (recurForDays === 1) addDays = recurForDays * inst - 1
    else if (recurForDays > 1 && inst > 1) addDays = recurForDays * inst - recurForDays
    // recurForDays > 1 && inst === 1 → 0; recurForDays === 0 → 0
    const end = new Date()
    end.setDate(end.getDate() + addDays)
    return Math.floor(end.getTime() / 1000)
}

/** Map form data to the reminder save payload (PUT /saveReminder). */
export function mapFormToStatusPreferencePayload(
    data: TemplateFormData,
    templateId: number,
    creatorId: number,
): SaveEmailStatusPreferencePayload {
    return {
        templateId,
        statusIds: data.statusIds,
        creatorId,
    }
}

export function mapFormToReminderPayload(
    data: TemplateFormData,
    templateId: number,
    reminderId = 0,
): SaveReminderPayload {
    return {
        reminderId,
        templateId,
        recurrenceId: data.reminderRecurrenceId,
        recurForDays: data.reminderRecurForDays,
        instances: data.reminderInstances,
        isSendingReminder: data.isSendingReminder,
        endDate: computeReminderEndDate(data.reminderRecurForDays, data.reminderInstances),
    }
}
